import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateSeoMutation } from '../../api/account/accountApiSlice';
import { TenantSeo } from '../../api/account/accountApiTypes';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

/** Mirrors the limits `updateSeo` enforces in new-backend/admin accountController.ts. */
const MAX_TITLE = 70;
const MAX_DESCRIPTION = 200;

type SeoSectionProps = {
    readOnly: boolean;
    /** The tenant's own values; null fields = derived from its name. */
    seo: TenantSeo | null;
    organizationName: string;
};

/**
 * The booking page's browser-tab title and description, also used when its
 * link is shared (preview) and by search engines.
 *
 * Optional. Without them the public config derives both from the tenant's name
 * (`resolveSeo` in new-backend/app) — the placeholders show exactly that, so a
 * tenant sees what applies before deciding to change it.
 */
const SeoSection = ({ readOnly, seo, organizationName }: SeoSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateSeo, { isLoading: saving }] = useUpdateSeoMutation();
    // One editable unit (design-decisions › Bearbeiten und Speichern). The view
    // is derived from the stored values on every render; the Account page
    // remounts this card per tenant, so a draft never crosses a switch.
    const draft = useEditableDraft<{ title: string, description: string }>({
        title: seo?.title ?? '',
        description: seo?.description ?? '',
    });
    const { title, description } = draft.value;
    const [error, setError] = useState<string | null>(null);

    const save = async () => {
        setError(null);
        try {
            await updateSeo({ title, description }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err) {
            setError(errorMessageFrom(err, t('account.seo.save_failed'), t));
        }
    };

    const inputClass = 'rounded-[5px] border border-foreground/15 px-3 py-2 text-sm text-foreground'
        + ' bg-transparent disabled:bg-foreground/5';

    /** The fields — locked in the view, editable in the draft. */
    const renderFields = (locked: boolean) => (
        <div className={'flex flex-col gap-3'}>
            <div className={'flex flex-col'}>
                <label className={'text-xs text-foreground/60 mb-1'} htmlFor={'seo-title'}>
                    {t('account.seo.page_title')}
                </label>
                <input
                    id={'seo-title'}
                    type={'text'}
                    value={title}
                    maxLength={MAX_TITLE}
                    placeholder={organizationName}
                    disabled={locked || saving}
                    onChange={(event) => draft.setDraft({ title: event.target.value, description })}
                    className={inputClass}
                />
            </div>
            <div className={'flex flex-col'}>
                <label className={'text-xs text-foreground/60 mb-1'} htmlFor={'seo-description'}>
                    {t('account.seo.description')}
                </label>
                <textarea
                    id={'seo-description'}
                    value={description}
                    maxLength={MAX_DESCRIPTION}
                    placeholder={t('account.seo.description_default', { organization: organizationName })}
                    disabled={locked || saving}
                    onChange={(event) => draft.setDraft({ title, description: event.target.value })}
                    className={`${inputClass} min-h-[72px]`}
                />
            </div>
            <p className={'text-xs text-foreground/60'}>{t('account.seo.blank_hint')}</p>
        </div>
    );

    return (
        <EditableSection
            id={'seo'}
            title={t('account.seo.title')}
            description={t('account.seo.intro')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            error={error}
            onEdit={() => { setError(null); draft.startEdit(); }}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void save(); }}
            view={renderFields(true)}
        >
            {renderFields(false)}
        </EditableSection>
    );
};

export default SeoSection;
